import {useTranslations} from 'next-intl';
import Container from '@/container';
import {Grid, Row, Col} from '@/grid';
import {SectionPadding} from '@/section';
import {withNeutral700, withNeutral800, withNeutral900, withPurple800} from '@/hocs/withColors';
import {withHomeSubP, withHeadingEyebrow} from '@/hocs/withFontType';
import {withMarginBottomMd, withMarginBottomXl, withMarginBottomXlToSm, withMarginTopMd} from '@/hocs/withSpacing';
import {H2, H3, P, PNoMargin} from '@/typography';
import {GridLayout, GridItem} from '@/ui/grid/grid-layout';
import BoxGrid from '@/ui/box-grid';
import HighlightBox from '@/ui/highlight-box';
import ModalButton from '@/ui/modal-button';
import PictureWrapper from '@/ui/images-wrapper';
import styles from './styles.module.scss';

const imgPath = '/static/images/home/workable-agent/';

const agentResponsiveImages = {
  desktopImageUrl: 'agent',
  mobileImageUrl: 'agent-mobile'
};

const emailResponseRatesResponsiveImages = {
  desktopImageUrl: 'email-response-rates-desktop',
  mobileImageUrl: 'email-response-rates-mobile'
};

const EyebrowNeutral700 = withNeutral700(withHeadingEyebrow(P));
const H2MarginedMd = withMarginBottomMd(H2);
const SubPNeutral800 = withNeutral800(withHomeSubP(PNoMargin));
const TitleBlock = withMarginBottomXl('div');
const VideoCtaBlock = withMarginTopMd('div');
const DivMargined = withMarginBottomXlToSm('div');
const H3Neutral900 = withNeutral900(H3);
const PNeutral800 = withNeutral800(PNoMargin);
const PNeutral800LinkNoMargin = withNeutral800(PNoMargin);

const H2Purple800 = withPurple800(H2);

const WorkableAgentIntro = () => {
  const t = useTranslations('homepage.workableAgent');
  const workableAiHeroT = useTranslations('workableAi.hero');
  const highlightStats = t.raw('highlightBox.items');

  return (
    <SectionPadding>
      <Container>
        <Grid fluid>
          <Row center="xs">
            <Col xs={12} md={10}>
              <EyebrowNeutral700>{t('eyebrow')}</EyebrowNeutral700>
              <H2MarginedMd>{t('title')}</H2MarginedMd>
              <TitleBlock>
                <SubPNeutral800>{t('subtitle')}</SubPNeutral800>
                <VideoCtaBlock>
                  <ModalButton
                    openName="openList2"
                    videoId="09fkqcgkpp"
                    variant="secondary-play-btn"
                    copyBtn={t('videoCta')}
                    ariaLabel={t('videoCtaAriaLabel')}
                    customAttributes={{
                      'data-gtm': 'generic-body-overview-video-ai'
                    }}
                    transcript={workableAiHeroT('transcript')}
                  />
                </VideoCtaBlock>
              </TitleBlock>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <DivMargined>
                <GridLayout Col={3} lgCol={2} autoRows>
                  <GridItem Order={1}>
                    <BoxGrid backgroundColor="var(--purple100)">
                      <H3Neutral900>{t('box1.title')}</H3Neutral900>
                      <PNeutral800>{t('box1.description')}</PNeutral800>
                    </BoxGrid>
                  </GridItem>
                  <GridItem Order={3} lgOrder={2}>
                    <BoxGrid backgroundColor="var(--purple100)">
                      <H3Neutral900>{t('box2.title')}</H3Neutral900>
                      <PNeutral800>{t('box2.description')}</PNeutral800>
                    </BoxGrid>
                  </GridItem>
                  <GridItem Order={2} lgOrder={3} Col={2} Row={2}>
                    <PictureWrapper
                      imgPath={imgPath}
                      responsiveImages={agentResponsiveImages}
                      imageTypeFallback="jpg"
                      altImage={t('mainImageAlt')}
                    />
                  </GridItem>
                </GridLayout>

                <GridLayout Col={3} lgCol={2} autoRows>
                  <GridItem>
                    <BoxGrid backgroundColor="var(--purple100)">
                      <H3Neutral900>{t('box3.title')}</H3Neutral900>
                      <PNeutral800>{t('box3.description')}</PNeutral800>
                    </BoxGrid>
                  </GridItem>
                  <GridItem>
                    <PictureWrapper
                      imgPath={imgPath}
                      responsiveImages={emailResponseRatesResponsiveImages}
                      imageTypeFallback="jpg"
                      altImage={t('emailResponseRatesImageAlt')}
                    />
                  </GridItem>
                  <GridItem>
                    <BoxGrid
                      backgroundColor="var(--purple100)"
                      Url={t('box5.url')}
                      UrlCopy={t('box5.urlCopy')}>
                      <H3Neutral900>{t('box5.title')}</H3Neutral900>
                      <PNeutral800LinkNoMargin>{t('box5.description')}</PNeutral800LinkNoMargin>
                    </BoxGrid>
                  </GridItem>
                </GridLayout>
              </DivMargined>

              <Row center="xs">
                <Col xs={12}>
                  <HighlightBox backgroundColor="var(--purple100)">
                    <div className={styles['highlight-stats']}>
                      {highlightStats.map(({title, label}) => (
                        <div key={title} className={styles['stat-item']}>
                          <H2Purple800 className={`heading-2 ${styles['stat-title']}`}>
                            {title}
                          </H2Purple800>
                          <PNeutral800>{label}</PNeutral800>
                        </div>
                      ))}
                    </div>
                  </HighlightBox>
                </Col>
              </Row>
            </Col>
          </Row>
        </Grid>
      </Container>
    </SectionPadding>
  );
};

export default WorkableAgentIntro;
